import { useEffect, useState } from "react";

import {
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Stethoscope,
  Search,
  CirclePlus,
  PencilLine,
  Trash2,
  X,
  Check,
  Plus
} from "lucide-react";

import { procedures } from "../data/procedures";

import {
  doc,
  getDoc,
  onSnapshot,
  setDoc
} from "../access/firestore";

import { db } from "../firebase";

import { useClinic } from "../context/ClinicContext";


export default function MobileProceduresSettings() {

  const { clinicId } = useClinic();

  const [expandedSpecialty, setExpandedSpecialty] =
    useState(null);

  const [search, setSearch] = useState("");

  const [localProcedures, setLocalProcedures] =
    useState([]);

  const [showModal, setShowModal] =
    useState(false);

  const [editingProcedure, setEditingProcedure] =
    useState(null);

  const [showDeleteDialog, setShowDeleteDialog] =
    useState(false);

  const [procedureToDelete, setProcedureToDelete] =
    useState(null);

  const [saving, setSaving] =
    useState(false);

  const [form, setForm] = useState({
    specialty: "",
    category: "",
    name: "",
    requiresOdontogram: false,
    allowsSurfaces: false
  });

  const [newCategory, setNewCategory] =
    useState("");

  const [creatingCategory, setCreatingCategory] =
    useState(false);


  /* =====================================================
     CARGAR PROCEDIMIENTOS
  ===================================================== */

  useEffect(() => {

    if (!clinicId) return;

    const ref = doc(
      db,
      "clinics",
      clinicId,
      "settings",
      "procedures"
    );

    const init = async () => {

      const snap = await getDoc(ref);

      if (!snap.exists()) {

        await setDoc(ref, {
          data: JSON.parse(
            JSON.stringify(procedures)
          )
        });

      }

    };

    init();

    const unsubscribe = onSnapshot(
      ref,
      snap => {

        if (!snap.exists()) return;

        const data = snap.data();

        setLocalProcedures(
          data.data || []
        );

      }
    );

    return () => unsubscribe();

  }, [clinicId]);


  /* =====================================================
     NUEVO PROCEDIMIENTO
  ===================================================== */

  function openCreateModal(specialty) {

    setEditingProcedure(null);

    setCreatingCategory(false);

    setNewCategory("");

    setForm({
      specialty: specialty.specialty,

      category:
        specialty.categories.length
          ? specialty.categories[0].name
          : "",

      name: "",

      requiresOdontogram: false,

      allowsSurfaces: false
    });

    setShowModal(true);
  }


  /* =====================================================
     EDITAR PROCEDIMIENTO
  ===================================================== */

  function openEditModal(
    specialty,
    category,
    procedure
  ) {

    setEditingProcedure(procedure);

    setCreatingCategory(false);

    setNewCategory("");

    setForm({

      specialty:
        specialty.specialty,

      category:
        category.name,

      name:
        procedure.name,

      requiresOdontogram:
        procedure.requiresOdontogram,

      allowsSurfaces:
        procedure.allowsSurfaces

    });

    setShowModal(true);

  }


  /* =====================================================
     ELIMINAR
  ===================================================== */

  function openDeleteDialog(procedure) {

    setProcedureToDelete(procedure);

    setShowDeleteDialog(true);

  }


  /* =====================================================
     GUARDAR
  ===================================================== */

  async function saveProcedure() {

    if (!form.name.trim()) {

      alert(
        "Escribe un nombre para el procedimiento."
      );

      return;
    }

    if (
      creatingCategory &&
      !newCategory.trim()
    ) {

      alert(
        "Escribe el nombre de la nueva categoría."
      );

      return;
    }

    if (!clinicId) return;

    setSaving(true);

    try {

      const copy = JSON.parse(
        JSON.stringify(localProcedures)
      );

      const specialty = copy.find(
        s =>
          s.specialty ===
          form.specialty
      );

      if (!specialty) return;

      let category =
        specialty.categories.find(
          c =>
            c.name ===
            form.category
        );

      if (
        creatingCategory &&
        newCategory.trim()
      ) {

        category = {

          name:
            newCategory.trim(),

          procedures: []

        };

        specialty.categories.push(
          category
        );

      }

      if (!category) return;

      if (editingProcedure) {

        const procedure =
          category.procedures.find(
            p =>
              p.id ===
              editingProcedure.id
          );

        if (!procedure) {

          setSaving(false);

          return;
        }

        procedure.name =
          form.name.trim();

        procedure.requiresOdontogram =
          form.requiresOdontogram;

        procedure.allowsSurfaces =
          form.allowsSurfaces;

      } else {

        category.procedures.push({

          id:
            crypto.randomUUID(),

          name:
            form.name.trim(),

          requiresOdontogram:
            form.requiresOdontogram,

          allowsSurfaces:
            form.allowsSurfaces

        });

      }

      await setDoc(

        doc(
          db,
          "clinics",
          clinicId,
          "settings",
          "procedures"
        ),

        {
          data: copy
        }

      );

      closeModal();

    } finally {

      setSaving(false);

    }

  }


  /* =====================================================
     ELIMINAR PROCEDIMIENTO
  ===================================================== */

  async function deleteProcedure() {

    if (!clinicId || !procedureToDelete)
      return;

    setSaving(true);

    try {

      const copy = JSON.parse(
        JSON.stringify(localProcedures)
      );

      copy.forEach(
        specialty => {

          specialty.categories.forEach(
            category => {

              category.procedures =
                category.procedures.filter(
                  procedure =>
                    procedure.id !==
                    procedureToDelete.id
                );

            }
          );

        }
      );

      await setDoc(

        doc(
          db,
          "clinics",
          clinicId,
          "settings",
          "procedures"
        ),

        {
          data: copy
        }

      );

      setShowDeleteDialog(false);

      setProcedureToDelete(null);

    } finally {

      setSaving(false);

    }

  }


  /* =====================================================
     CERRAR MODAL
  ===================================================== */

  function closeModal() {

    setShowModal(false);

    setEditingProcedure(null);

    setCreatingCategory(false);

    setNewCategory("");

    setForm({

      specialty: "",
      category: "",
      name: "",
      requiresOdontogram: false,
      allowsSurfaces: false

    });

  }


  /* =====================================================
     FILTRO
  ===================================================== */

  const searchText =
    search.trim().toLowerCase();


  const specialties =
    localProcedures.filter(
      specialty =>

        specialty.categories.some(
          category =>

            category.procedures.some(
              procedure =>

                procedure.name
                  .toLowerCase()
                  .includes(searchText)

            )

        )
    );


  return (

    <div
      style={{
        paddingBottom: 30
      }}
    >

      {/* =================================================
          ENCABEZADO
      ================================================= */}

      <div
        style={{
          marginBottom: 18
        }}
      >

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12
          }}
        >

          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 14,
              background: "#eff6ff",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >

            <Stethoscope
              size={23}
              strokeWidth={1.8}
            />

          </div>

          <div>

            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#0f172a"
              }}
            >
              Procedimientos
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 13,
                color: "#64748b",
                lineHeight: 1.4
              }}
            >
              Especialidades y procedimientos
            </div>

          </div>

        </div>

      </div>


      {/* =================================================
          BUSCADOR
      ================================================= */}

      <div
        style={{
          position: "relative",
          marginBottom: 18
        }}
      >

        <Search
          size={19}
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform:
              "translateY(-50%)",
            color: "#94a3b8"
          }}
        />

        <input
          value={search}
          onChange={e =>
            setSearch(e.target.value)
          }
          placeholder="Buscar procedimiento..."
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding:
              "13px 14px 13px 43px",
            border:
              "1px solid #dbe2ea",
            borderRadius: 14,
            background: "#fff",
            fontSize: 14,
            outline: "none"
          }}
        />

      </div>


      {/* =================================================
          ESPECIALIDADES
      ================================================= */}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12
        }}
      >

        {specialties.map(
          specialty => {

            const expanded =
              expandedSpecialty ===
              specialty.specialty;

            const categories =
              specialty.categories.filter(
                category =>

                  category.procedures.some(
                    procedure =>

                      procedure.name
                        .toLowerCase()
                        .includes(searchText)

                  )
              );

            return (

              <div
                key={
                  specialty.specialty
                }
                style={{
                  background: "#fff",
                  border:
                    "1px solid #e2e8f0",
                  borderRadius: 18,
                  overflow: "hidden"
                }}
              >

                {/* ESPECIALIDAD */}

                <button
                  type="button"
                  onClick={() =>
                    setExpandedSpecialty(
                      expanded
                        ? null
                        : specialty.specialty
                    )
                  }
                  style={{
                    width: "100%",
                    border: "none",
                    background:
                      expanded
                        ? "#f8fafc"
                        : "#fff",
                    padding: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      "space-between",
                    textAlign: "left",
                    cursor: "pointer"
                  }}
                >

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12
                    }}
                  >

                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 13,
                        background:
                          "#eff6ff",
                        color:
                          "#2563eb",
                        display: "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        flexShrink: 0
                      }}
                    >

                      <Stethoscope
                        size={21}
                        strokeWidth={1.8}
                      />

                    </div>

                    <div>

                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 800,
                          color:
                            "#0f172a"
                        }}
                      >
                        {
                          specialty.specialty
                        }
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 12,
                          color:
                            "#64748b"
                        }}
                      >
                        {
                          specialty.categories
                            .reduce(
                              (
                                total,
                                category
                              ) =>
                                total +
                                category
                                  .procedures
                                  .length,
                              0
                            )
                        }{" "}
                        procedimientos
                      </div>

                    </div>

                  </div>

                  <div
                    style={{
                      color: "#94a3b8"
                    }}
                  >

                    {expanded ? (
                      <ChevronDown
                        size={21}
                      />
                    ) : (
                      <ChevronRight
                        size={21}
                      />
                    )}

                  </div>

                </button>


                {/* CONTENIDO */}

                {expanded && (

                  <div
                    style={{
                      padding:
                        "0 12px 14px"
                    }}
                  >

                    {categories.map(
                      category => (

                        <div
                          key={
                            category.name
                          }
                          style={{
                            marginTop: 10,
                            border:
                              "1px solid #e2e8f0",
                            borderRadius: 15,
                            overflow:
                              "hidden"
                          }}
                        >

                          {/* CATEGORÍA */}

                          <div
                            style={{
                              padding: 14,
                              background:
                                "#f8fafc",
                              display:
                                "flex",
                              alignItems:
                                "center",
                              justifyContent:
                                "space-between",
                              gap: 10
                            }}
                          >

                            <div
                              style={{
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                gap: 9,
                                minWidth: 0
                              }}
                            >

                              <FolderOpen
                                size={18}
                                color="#2563eb"
                                strokeWidth={1.8}
                              />

                              <span
                                style={{
                                  fontSize: 14,
                                  fontWeight: 800,
                                  color:
                                    "#1e293b"
                                }}
                              >
                                {
                                  category.name
                                }
                              </span>

                            </div>

                            <span
                              style={{
                                fontSize: 11,
                                color:
                                  "#64748b",
                                background:
                                  "#fff",
                                border:
                                  "1px solid #e2e8f0",
                                padding:
                                  "4px 8px",
                                borderRadius:
                                  999,
                                whiteSpace:
                                  "nowrap"
                              }}
                            >
                              {
                                category
                                  .procedures
                                  .length
                              }
                            </span>

                          </div>


                          {/* PROCEDIMIENTOS */}

                          <div
                            style={{
                              padding: 10,
                              display:
                                "flex",
                              flexDirection:
                                "column",
                              gap: 8
                            }}
                          >

                            {category.procedures
                              .filter(
                                procedure =>
                                  procedure.name
                                    .toLowerCase()
                                    .includes(
                                      searchText
                                    )
                              )
                              .map(
                                procedure => (

                                  <div
                                    key={
                                      procedure.id
                                    }
                                    style={{
                                      padding: 13,
                                      border:
                                        "1px solid #e5e7eb",
                                      borderRadius: 13,
                                      background:
                                        "#fff"
                                    }}
                                  >

                                    <div
                                      style={{
                                        display:
                                          "flex",
                                        alignItems:
                                          "flex-start",
                                        justifyContent:
                                          "space-between",
                                        gap: 10
                                      }}
                                    >

                                      <div
                                        style={{
                                          minWidth: 0,
                                          flex: 1
                                        }}
                                      >

                                        <div
                                          style={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                            color:
                                              "#0f172a",
                                            lineHeight:
                                              1.4
                                          }}
                                        >
                                          {
                                            procedure.name
                                          }
                                        </div>


                                        <div
                                          style={{
                                            display:
                                              "flex",
                                            flexWrap:
                                              "wrap",
                                            gap: 6,
                                            marginTop: 8
                                          }}
                                        >

                                          {procedure.requiresOdontogram && (

                                            <span
                                              style={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                color:
                                                  "#2563eb",
                                                background:
                                                  "#eff6ff",
                                                padding:
                                                  "4px 7px",
                                                borderRadius:
                                                  999
                                              }}
                                            >
                                              Odontograma
                                            </span>

                                          )}

                                          {procedure.allowsSurfaces && (

                                            <span
                                              style={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                color:
                                                  "#475569",
                                                background:
                                                  "#f1f5f9",
                                                padding:
                                                  "4px 7px",
                                                borderRadius:
                                                  999
                                              }}
                                            >
                                              Superficies
                                            </span>

                                          )}

                                        </div>

                                      </div>


                                      {/* ACCIONES */}

                                      <div
                                        style={{
                                          display:
                                            "flex",
                                          gap: 5
                                        }}
                                      >

                                        <button
                                          type="button"
                                          onClick={() =>
                                            openEditModal(
                                              specialty,
                                              category,
                                              procedure
                                            )
                                          }
                                          style={{
                                            width: 36,
                                            height: 36,
                                            border:
                                              "1px solid #dbeafe",
                                            borderRadius:
                                              10,
                                            background:
                                              "#eff6ff",
                                            color:
                                              "#2563eb",
                                            display:
                                              "flex",
                                            alignItems:
                                              "center",
                                            justifyContent:
                                              "center"
                                          }}
                                        >

                                          <PencilLine
                                            size={16}
                                          />

                                        </button>

                                        <button
                                          type="button"
                                          onClick={() =>
                                            openDeleteDialog(
                                              procedure
                                            )
                                          }
                                          style={{
                                            width: 36,
                                            height: 36,
                                            border:
                                              "1px solid #fee2e2",
                                            borderRadius:
                                              10,
                                            background:
                                              "#fef2f2",
                                            color:
                                              "#dc2626",
                                            display:
                                              "flex",
                                            alignItems:
                                              "center",
                                            justifyContent:
                                              "center"
                                          }}
                                        >

                                          <Trash2
                                            size={16}
                                          />

                                        </button>

                                      </div>

                                    </div>

                                  </div>

                                )
                              )}

                          </div>

                        </div>

                      )
                    )}


                    {/* AGREGAR */}

                    <button
                      type="button"
                      onClick={() =>
                        openCreateModal(
                          specialty
                        )
                      }
                      style={{
                        width: "100%",
                        marginTop: 10,
                        minHeight: 46,
                        border: "none",
                        borderRadius: 13,
                        background:
                          "#2563eb",
                        color: "#fff",
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        gap: 8,
                        fontSize: 13,
                        fontWeight: 700
                      }}
                    >

                      <CirclePlus
                        size={18}
                      />

                      Agregar procedimiento

                    </button>

                  </div>

                )}

              </div>

            );

          }
        )}

      </div>


      {/* =================================================
          SIN RESULTADOS
      ================================================= */}

      {!specialties.length && (

        <div
          style={{
            background: "#fff",
            border:
              "1px solid #e2e8f0",
            borderRadius: 16,
            padding: 28,
            textAlign: "center",
            color: "#64748b",
            fontSize: 14
          }}
        >

          No se encontraron procedimientos.

        </div>

      )}


      {/* =================================================
          MODAL CREAR / EDITAR
      ================================================= */}

      {showModal && (

        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,.55)",
            zIndex: 99999,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center"
          }}
        >

          <div
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "92vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius:
                "22px 22px 0 0",
              padding: 20,
              boxSizing: "border-box"
            }}
          >

            {/* HEADER */}

            <div
              style={{
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
                marginBottom: 20
              }}
            >

              <div>

                <div
                  style={{
                    fontSize: 19,
                    fontWeight: 800,
                    color:
                      "#0f172a"
                  }}
                >
                  {editingProcedure
                    ? "Editar procedimiento"
                    : "Nuevo procedimiento"}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color:
                      "#64748b"
                  }}
                >
                  Configuración del procedimiento
                </div>

              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  width: 38,
                  height: 38,
                  border: "none",
                  borderRadius: 11,
                  background:
                    "#f1f5f9",
                  color:
                    "#475569",
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center"
                }}
              >

                <X size={19} />

              </button>

            </div>


            {/* ESPECIALIDAD */}

            <FieldLabel>
              Especialidad
            </FieldLabel>

            <select
              value={form.specialty}
              onChange={e =>
                setForm(prev => ({
                  ...prev,
                  specialty:
                    e.target.value,
                  category: ""
                }))
              }
              disabled={Boolean(
                editingProcedure
              )}
              style={selectStyle}
            >

              {localProcedures.map(
                specialty => (

                  <option
                    key={
                      specialty.specialty
                    }
                    value={
                      specialty.specialty
                    }
                  >
                    {
                      specialty.specialty
                    }
                  </option>

                )
              )}

            </select>


            {/* CATEGORÍA */}

            <div
              style={{
                marginTop: 16
              }}
            >

              <FieldLabel>
                Categoría
              </FieldLabel>

              <select
                value={form.category}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    category:
                      e.target.value
                  }))
                }
                disabled={
                  creatingCategory
                }
                style={selectStyle}
              >

                {localProcedures
                  .find(
                    s =>
                      s.specialty ===
                      form.specialty
                  )
                  ?.categories
                  .map(category => (

                    <option
                      key={
                        category.name
                      }
                      value={
                        category.name
                      }
                    >
                      {
                        category.name
                      }
                    </option>

                  ))}

              </select>


              <button
                type="button"
                onClick={() =>
                  setCreatingCategory(
                    prev => !prev
                  )
                }
                style={{
                  marginTop: 10,
                  border: "none",
                  background:
                    "transparent",
                  color:
                    "#2563eb",
                  fontSize: 13,
                  fontWeight: 700,
                  padding: 0,
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 5
                }}
              >

                <Plus size={15} />

                Crear nueva categoría

              </button>


              {creatingCategory && (

                <input
                  value={newCategory}
                  onChange={e =>
                    setNewCategory(
                      e.target.value
                    )
                  }
                  placeholder="Nombre de la nueva categoría"
                  style={{
                    ...inputStyle,
                    marginTop: 10
                  }}
                />

              )}

            </div>


            {/* NOMBRE */}

            <div
              style={{
                marginTop: 16
              }}
            >

              <FieldLabel>
                Nombre
              </FieldLabel>

              <input
                value={form.name}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    name:
                      e.target.value
                  }))
                }
                placeholder="Ej. Limpieza dental"
                style={inputStyle}
              />

            </div>


            {/* OPCIONES */}

            <div
              style={{
                marginTop: 20,
                display: "flex",
                flexDirection:
                  "column",
                gap: 10
              }}
            >

              <ToggleRow
                label="Requiere odontograma"
                checked={
                  form.requiresOdontogram
                }
                onChange={value =>
                  setForm(prev => ({
                    ...prev,
                    requiresOdontogram:
                      value
                  }))
                }
              />

              <ToggleRow
                label="Permite superficies"
                checked={
                  form.allowsSurfaces
                }
                onChange={value =>
                  setForm(prev => ({
                    ...prev,
                    allowsSurfaces:
                      value
                  }))
                }
              />

            </div>


            {/* BOTONES */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 10,
                marginTop: 24
              }}
            >

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                style={{
                  minHeight: 46,
                  border:
                    "1px solid #dbe2ea",
                  borderRadius: 12,
                  background:
                    "#fff",
                  color:
                    "#475569",
                  fontWeight: 700
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={saveProcedure}
                disabled={saving}
                style={{
                  minHeight: 46,
                  border: "none",
                  borderRadius: 12,
                  background:
                    "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  gap: 7
                }}
              >

                <Check size={18} />

                {saving
                  ? "Guardando..."
                  : "Guardar"}

              </button>

            </div>

          </div>

        </div>

      )}


      {/* =================================================
          CONFIRMAR ELIMINACIÓN
      ================================================= */}

      {showDeleteDialog && (

        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,.55)",
            zIndex: 100000,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center"
          }}
        >

          <div
            style={{
              width: "100%",
              maxWidth: 520,
              background: "#fff",
              borderRadius:
                "22px 22px 0 0",
              padding: 22,
              boxSizing: "border-box"
            }}
          >

            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 14,
                background:
                  "#fef2f2",
                color:
                  "#dc2626",
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "center",
                marginBottom: 14
              }}
            >

              <Trash2 size={22} />

            </div>

            <div
              style={{
                fontSize: 19,
                fontWeight: 800,
                color:
                  "#0f172a"
              }}
            >
              Eliminar procedimiento
            </div>

            <div
              style={{
                marginTop: 8,
                fontSize: 14,
                color:
                  "#64748b",
                lineHeight: 1.5
              }}
            >
              ¿Deseas eliminar{" "}
              <strong
                style={{
                  color:
                    "#334155"
                }}
              >
                {procedureToDelete?.name}
              </strong>
              ?
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "1fr 1fr",
                gap: 10,
                marginTop: 22
              }}
            >

              <button
                type="button"
                onClick={() =>
                  setShowDeleteDialog(false)
                }
                disabled={saving}
                style={{
                  minHeight: 46,
                  border:
                    "1px solid #dbe2ea",
                  borderRadius: 12,
                  background:
                    "#fff",
                  color:
                    "#475569",
                  fontWeight: 700
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={deleteProcedure}
                disabled={saving}
                style={{
                  minHeight: 46,
                  border: "none",
                  borderRadius: 12,
                  background:
                    "#dc2626",
                  color: "#fff",
                  fontWeight: 700,
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  gap: 7
                }}
              >

                <Trash2 size={17} />

                {saving
                  ? "Eliminando..."
                  : "Eliminar"}

              </button>

            </div>

          </div>

        </div>

      )}

    </div>

  );
}


/* =====================================================
   COMPONENTES AUXILIARES
===================================================== */

function FieldLabel({ children }) {

  return (

    <label
      style={{
        display: "block",
        marginBottom: 7,
        fontSize: 12,
        fontWeight: 700,
        color: "#64748b"
      }}
    >
      {children}
    </label>

  );

}


function ToggleRow({
  label,
  checked,
  onChange
}) {

  return (

    <button
      type="button"
      onClick={() =>
        onChange(!checked)
      }
      style={{
        width: "100%",
        border:
          "1px solid #e2e8f0",
        borderRadius: 13,
        background: "#fff",
        padding:
          "12px 13px",
        display: "flex",
        alignItems: "center",
        justifyContent:
          "space-between",
        textAlign: "left"
      }}
    >

      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#334155"
        }}
      >
        {label}
      </span>

      <span
        style={{
          width: 42,
          height: 24,
          borderRadius: 999,
          background:
            checked
              ? "#2563eb"
              : "#cbd5e1",
          padding: 2,
          boxSizing: "border-box",
          display: "flex",
          justifyContent:
            checked
              ? "flex-end"
              : "flex-start",
          transition:
            "all .2s"
        }}
      >

        <span
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            boxShadow:
              "0 1px 3px rgba(0,0,0,.2)"
          }}
        />

      </span>

    </button>

  );

}


const inputStyle = {

  width: "100%",

  boxSizing: "border-box",

  padding: "12px 13px",

  border:
    "1px solid #dbe2ea",

  borderRadius: 12,

  background: "#fff",

  fontSize: 14,

  outline: "none"

};


const selectStyle = {

  width: "100%",

  boxSizing: "border-box",

  padding: "12px 13px",

  border:
    "1px solid #dbe2ea",

  borderRadius: 12,

  background: "#fff",

  fontSize: 14,

  outline: "none"

};